import { useState } from 'react';
import {
    Box,
    Badge,
    Icon,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Portal,
    VStack,
    HStack,
    Text,
    Flex,
    Spinner,
} from '@chakra-ui/react';
import { TbMessageCircle } from 'react-icons/tb';
import { useGetOrderNotesPreview } from '../../../hooks/useGetOrderNotesPreview';

interface NoteCountBadgeProps {
    noteCount: number;
    orderId: string;
}

const formatPreviewDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

const NoteCountBadge = ({ noteCount, orderId }: NoteCountBadgeProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { data: notes, isLoading } = useGetOrderNotesPreview(orderId, isOpen);

    if (noteCount <= 0) return null;

    return (
        <Popover
            trigger="hover"
            isLazy
            openDelay={400}
            closeDelay={200}
            placement="bottom-start"
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
        >
            <PopoverTrigger>
                <Box
                    position="relative"
                    display="inline-flex"
                    cursor="pointer"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Icon
                        as={TbMessageCircle as React.ElementType}
                        w="16px"
                        h="16px"
                        color="gray.400"
                    />
                    <Badge
                        position="absolute"
                        top="-4px"
                        right="-6px"
                        bg="#0071E3"
                        color="white"
                        borderRadius="full"
                        minW="14px"
                        h="14px"
                        fontSize="9px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        lineHeight={1}
                    >
                        {noteCount > 99 ? '99+' : noteCount}
                    </Badge>
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    w="320px"
                    maxH="300px"
                    borderRadius="lg"
                    boxShadow="none"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box
                        px={3}
                        py={2}
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                    >
                        <HStack spacing={1.5}>
                            <Icon as={TbMessageCircle as React.ElementType} w={4} h={4} color="gray.600" />
                            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                                Messages ({noteCount})
                            </Text>
                        </HStack>
                    </Box>
                    <PopoverBody p={0} overflowY="auto" maxH="256px">
                        {isLoading ? (
                            <Flex justify="center" align="center" py={6}>
                                <Spinner size="sm" color="gray.400" />
                            </Flex>
                        ) : !notes || notes.length === 0 ? (
                            <Flex justify="center" align="center" py={6} direction="column">
                                <Icon as={TbMessageCircle as React.ElementType} w={6} h={6} color="gray.300" mb={1} />
                                <Text fontSize="xs" color="gray.400">No messages</Text>
                            </Flex>
                        ) : (
                            <VStack spacing={0} align="stretch" py={1}>
                                {notes.map((note) => (
                                    <Box
                                        key={note._id}
                                        px={3}
                                        py={2}
                                        _hover={{ bg: 'gray.50' }}
                                    >
                                        <HStack justify="space-between" mb={0.5}>
                                            <Text fontSize="xs" fontWeight="semibold" color="#0071E3">
                                                {note.user}
                                            </Text>
                                            <Text fontSize="10px" color="gray.400" flexShrink={0}>
                                                {formatPreviewDate(note.timestamp)}
                                            </Text>
                                        </HStack>
                                        <Text
                                            fontSize="xs"
                                            color="gray.600"
                                            noOfLines={2}
                                            lineHeight="1.4"
                                        >
                                            {note.note}
                                        </Text>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export default NoteCountBadge;
